import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useBuddyVoice } from '@/lib/voice';
import { useSEO } from '@/lib/seo';
import {
  Camera, Loader2, X, AlertTriangle, ShieldCheck, GraduationCap, Sparkles,
  Cpu, Zap, RefreshCw, ArrowRight,
} from 'lucide-react';

async function fileToBase64(file) {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function NameplatePage() {
  useSEO({
    title: 'Nameplate Scanner — DIY X-treme | Read Motor, Breaker, HVAC Specs',
    description: 'Snap a photo of any motor, breaker, HVAC unit, or appliance nameplate. Our AI instantly parses voltage, amperage, horsepower, model, and safety specs. Save them to your truck.',
    canonical: 'https://diybot.org/nameplate',
  });
  const [photoDataUrl, setPhotoDataUrl] = useState(null);
  const [photoMime, setPhotoMime] = useState(null);
  const [notes, setNotes] = useState('');
  const [tag, setTag] = useState('');
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [usage, setUsage] = useState(null);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const inputRef = useRef(null);
  const { speakQuip, stop: stopVoice } = useBuddyVoice();

  useEffect(() => {
    loadUsage();
    loadHistory();
    return () => stopVoice();
  }, [stopVoice]);

  const loadUsage = async () => {
    try {
      const res = await api.get('/nameplate/usage');
      setUsage(res.data);
    } catch (e) {
      console.warn('Nameplate usage fetch failed:', e);
    }
  };

  const loadHistory = async () => {
    try {
      const res = await api.get('/nameplates');
      setHistory(res.data);
    } catch (e) {
      console.warn('Nameplate history fetch failed:', e);
    }
  };

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(png|jpeg|jpg|webp)$/i.test(file.type)) {
      setError('Photo must be PNG, JPEG, or WEBP');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError('Photo must be under 8MB');
      return;
    }
    setError('');
    const dataUrl = await fileToBase64(file);
    setPhotoDataUrl(dataUrl);
    setPhotoMime(file.type);
    setResult(null);
  };

  const clearPhoto = () => {
    setPhotoDataUrl(null);
    setPhotoMime(null);
    setResult(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const scan = async () => {
    setError('');
    if (!photoDataUrl) {
      setError('Take or upload a photo of the nameplate first.');
      return;
    }
    setScanning(true);
    try {
      const res = await api.post('/nameplate', {
        image_base64: photoDataUrl,
        image_mime: photoMime,
        notes: notes.trim() || null,
        tag: tag.trim() || null,
      }, { timeout: 60 * 1000 });
      setResult(res.data);
      if (res.data.usage) setUsage(res.data.usage);
      loadHistory();
      // Buddy reacts to the scan with a contextual line
      const specs = res.data?.parsed || res.data?.specs || {};
      speakQuip('nameplate_scan', {
        equipment: specs.equipment_type || specs.type || null,
        brand: specs.brand || specs.manufacturer || null,
        voltage: specs.voltage || null,
      });
    } catch (e) {
      const detail = e.response?.data?.detail || 'Scan failed. Try again.';
      setError(detail);
    } finally {
      setScanning(false);
    }
  };

  const outOfScans = usage && !usage.is_unlimited && usage.remaining === 0;

  return (
    <div className="max-w-4xl mx-auto px-6 py-10">
      <div className="mb-8 fade-up">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-sky-400 mb-2 flex items-center gap-2">
          <Cpu size={12} /> Nameplate Scanner
        </div>
        <h1 className="font-display text-5xl sm:text-6xl text-zinc-100 tracking-tight leading-none">
          READ ANY <span className="text-gradient-cool">NAMEPLATE</span>
        </h1>
        <p className="text-zinc-400 mt-3 text-lg max-w-2xl">
          Snap a photo of a motor, breaker, HVAC unit, or any equipment tag. Get every spec explained — voltage, phase, amps, safety notes, and what it all means.
        </p>
      </div>

      {/* Usage banner */}
      {usage && (
        <div
          data-testid="nameplate-usage"
          className={`industrial-card !p-4 mb-6 border-l-4 ${
            usage.is_unlimited
              ? 'border-l-lime-400 bg-gradient-to-r from-lime-500/5 to-transparent'
              : outOfScans
                ? 'border-l-red-500 bg-red-500/5'
                : 'border-l-amber-400 bg-gradient-to-r from-amber-500/5 to-transparent'
          } flex flex-col sm:flex-row sm:items-center justify-between gap-3`}
        >
          <div className="flex items-center gap-3">
            {usage.is_unlimited ? (
              <ShieldCheck size={22} className="text-lime-400 shrink-0" />
            ) : (
              <Sparkles size={22} className="text-amber-400 pulse-gentle shrink-0" />
            )}
            <div className="text-sm">
              {usage.is_unlimited ? (
                <span className="text-lime-400 font-mono uppercase tracking-widest text-xs">
                  Pro · Unlimited scans
                </span>
              ) : outOfScans ? (
                <span className="text-red-400">
                  You've used all <b>{usage.weekly_limit}</b> free scans this week. Upgrade for unlimited.
                </span>
              ) : (
                <span className="text-zinc-200">
                  <b>{usage.remaining}</b> of <b>{usage.weekly_limit}</b> free scans left this week
                </span>
              )}
            </div>
          </div>
          {!usage.is_unlimited && (
            <Link
              to="/account/subscribe"
              className={outOfScans ? 'btn-primary text-xs' : 'btn-secondary text-xs'}
            >
              <GraduationCap size={14} /> Go Pro <ArrowRight size={12} />
            </Link>
          )}
        </div>
      )}

      {/* Upload / Camera panel */}
      <div className="industrial-card border-t-4 border-t-sky-400 space-y-4 mb-6">
        {!photoDataUrl ? (
          <label className="flex flex-col items-center justify-center border-2 border-dashed border-zinc-700 hover:border-sky-500 hover:bg-sky-500/5 cursor-pointer py-14 transition-colors">
            <Camera size={40} className="text-sky-400 mb-3" strokeWidth={1.8} />
            <div className="font-display text-xl text-zinc-100 tracking-wide">
              TAP TO TAKE / UPLOAD PHOTO
            </div>
            <div className="text-zinc-500 text-sm font-mono uppercase tracking-widest mt-2">
              PNG · JPEG · WEBP · max 8MB
            </div>
            <input
              ref={inputRef}
              data-testid="nameplate-photo-input"
              type="file"
              accept="image/png,image/jpeg,image/webp"
              capture="environment"
              className="hidden"
              onChange={handlePhoto}
            />
          </label>
        ) : (
          <div className="space-y-3">
            <div className="relative inline-block border border-zinc-800 p-2 bg-black">
              <img
                data-testid="nameplate-preview"
                src={photoDataUrl}
                alt="Nameplate preview"
                className="max-h-64 object-contain mx-auto"
              />
              <button
                onClick={clearPhoto}
                className="absolute -top-3 -right-3 bg-zinc-900 border border-zinc-700 hover:border-red-500 hover:text-red-500 w-8 h-8 flex items-center justify-center"
                type="button"
                aria-label="Remove photo"
              >
                <X size={16} />
              </button>
            </div>
            <input
              data-testid="nameplate-notes"
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional: any context you know (brand, location, etc.)"
              className="industrial-input !py-2 text-sm"
              maxLength={200}
            />
            <input
              data-testid="nameplate-tag"
              type="text"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              placeholder="Save to truck / job site tag (e.g., '123 Main St', 'Truck 2')"
              className="industrial-input !py-2 text-sm"
              maxLength={80}
            />
            <button
              data-testid="nameplate-scan-btn"
              onClick={scan}
              disabled={scanning || outOfScans}
              className="btn-lime w-full hover-wiggle"
            >
              {scanning ? (
                <><Loader2 size={18} className="animate-spin" /> Reading nameplate…</>
              ) : outOfScans ? (
                <>Out of free scans — upgrade to continue</>
              ) : (
                <><Cpu size={18} /> Read This Nameplate</>
              )}
            </button>
          </div>
        )}

        {error && (
          <div data-testid="nameplate-error" className="border border-red-500/40 bg-red-500/10 text-red-400 px-3 py-2 text-sm font-mono flex items-center gap-2">
            <AlertTriangle size={16} /> {error}
          </div>
        )}
      </div>

      {/* Result */}
      {result && (result.unreadable ? (
        <div data-testid="nameplate-unreadable" className="industrial-card border-t-4 border-t-amber-400 !p-6 mb-6">
          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle size={22} className="text-amber-400" />
            <div className="font-display text-2xl text-zinc-100 tracking-wide">CAN'T READ IT</div>
          </div>
          <p className="text-zinc-300 text-sm">{result.unreadable}</p>
        </div>
      ) : (
        <NameplateResult data={result} />
      ))}

      {/* History */}
      {history.length > 0 && (
        <div className="mt-8">
          <button
            onClick={() => setShowHistory((v) => !v)}
            className="font-mono text-xs uppercase tracking-widest text-zinc-400 hover:text-amber-400 flex items-center gap-2"
          >
            <RefreshCw size={12} /> {showHistory ? 'Hide' : 'Show'} scan history ({history.length})
          </button>
          {showHistory && (
            <div data-testid="nameplate-history" className="mt-4 grid sm:grid-cols-2 gap-3">
              {history.map((h) => (
                <div key={h.id} className="industrial-card !p-4">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-sky-400 mb-1 flex items-center justify-between gap-2">
                    <span>{new Date(h.created_at).toLocaleString()}</span>
                    {h.tag && (
                      <span className="bg-amber-500/10 text-amber-400 border border-amber-500/30 px-1.5 py-0.5 text-[9px] truncate max-w-[50%]">
                        🚚 {h.tag}
                      </span>
                    )}
                  </div>
                  <div className="font-display text-lg text-zinc-100 tracking-wide leading-tight">
                    {h.result?.item_type || 'Nameplate'}
                  </div>
                  <div className="text-zinc-500 text-xs mt-0.5">
                    {[h.result?.manufacturer, h.result?.model].filter(Boolean).join(' · ') || '—'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function NameplateResult({ data }) {
  return (
    <div data-testid="nameplate-result" className="industrial-card border-t-4 border-t-lime-400 !p-6 mb-6 space-y-5 glow-lime">
      <div>
        <div className="font-mono text-xs uppercase tracking-widest text-lime-400 mb-1 flex items-center gap-2">
          <Zap size={12} /> Read · Confidence: {data.identified_confidence || 'medium'}
        </div>
        <h2 className="font-display text-3xl text-zinc-100 tracking-tight">
          {data.item_type || 'Equipment'}
        </h2>
        <div className="text-zinc-400 text-sm mt-1">
          {[data.manufacturer, data.model, data.serial && `S/N ${data.serial}`].filter(Boolean).join(' · ') || 'Manufacturer unknown'}
        </div>
      </div>

      {data.what_it_means && (
        <div className="border-l-4 border-l-sky-400 pl-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-sky-400 mb-1">
            What This Is
          </div>
          <p className="text-zinc-300 text-sm leading-relaxed">{data.what_it_means}</p>
        </div>
      )}

      {data.specs?.length > 0 && (
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-amber-400 mb-3">Specs</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {data.specs.map((s) => (
              <div key={`${s.label}-${s.value}`} className="border border-zinc-800 p-3">
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{s.label}</div>
                <div className="text-zinc-100 font-semibold text-sm mt-0.5">{s.value}</div>
                {s.note && <div className="text-zinc-500 text-xs mt-1 italic">{s.note}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {data.safety_notes?.length > 0 && (
        <div className="border-l-4 border-l-red-500 pl-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-red-400 mb-2 flex items-center gap-1">
            <AlertTriangle size={11} /> Safety Notes
          </div>
          <ul className="space-y-1 text-zinc-300 text-sm">
            {data.safety_notes.map((n) => (
              <li key={n} className="flex gap-2">
                <span className="text-red-400 font-mono shrink-0">›</span> {n}
              </li>
            ))}
          </ul>
        </div>
      )}

      {data.questions_a_pro_would_ask?.length > 0 && (
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
            Smart Follow-Up Questions
          </div>
          <ul className="space-y-1 text-zinc-400 text-sm italic">
            {data.questions_a_pro_would_ask.map((q) => (
              <li key={q} className="flex gap-2">
                <span className="text-amber-400 font-mono shrink-0">?</span> {q}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
